// src/components/WaveformComparison.tsx

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { AudioWithUrl } from '../types/audio.types';

interface WaveformComparisonProps {
  audios: AudioWithUrl[];
  startTime?: number;
  endTime?: number;
}

interface WaveformData {
  normalized: number[];
  loading: boolean;
  error: string | null;
}

const WaveformComparison: React.FC<WaveformComparisonProps> = ({
  audios,
  startTime = 2,
  endTime = 4,
}) => {
  const [waveforms, setWaveforms] = useState<Map<string, WaveformData>>(new Map());
  const [zoomLevel, setZoomLevel] = useState(1); // 1x, 2x, 3x, 4x
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const scrollContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Remove duplicatas baseado no audioId
  const uniqueAudios = useMemo(() => {
    const seen = new Set<string>();
    const unique: AudioWithUrl[] = [];
    
    for (const audio of audios) {
      if (!seen.has(audio.audioId)) {
        seen.add(audio.audioId);
        unique.push(audio);
      }
    }
    
    // Log de aviso se houver duplicatas
    if (unique.length !== audios.length) {
      console.warn(
        `[WaveformComparison] Removidas ${audios.length - unique.length} duplicatas. ` +
        `Total original: ${audios.length}, único: ${unique.length}`
      );
    }
    
    return unique;
  }, [audios]);

  // Cores para diferentes waveforms (visualmente distintas e acessíveis)
  const colors = useMemo(() => [
    '#4F46E5', // Indigo
    '#059669', // Green
    '#DC2626', // Red
    '#D97706', // Amber
    '#7C3AED', // Purple
    '#0891B2', // Cyan
  ], []);

  // Largura base do canvas (ajustada pelo zoom)
  const baseCanvasWidth = 2400;
  const canvasWidth = baseCanvasWidth * zoomLevel;

  // Processar áudio e extrair waveform
  const processAudioWaveform = useCallback(async (audio: AudioWithUrl): Promise<number[]> => {
    if (!audio.signedUrl) {
      throw new Error('URL do áudio não disponível');
    }

    try {
      // Fetch audio file
      const response = await fetch(audio.signedUrl);
      const arrayBuffer = await response.arrayBuffer();

      // Decode audio
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Extract channel data
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;

      // Calculate sample indices for time range
      const startSample = Math.floor(startTime * sampleRate);
      const endSample = Math.floor(endTime * sampleRate);
      const samples = channelData.slice(startSample, endSample);

      // Downsample for visualization (mais pontos para melhor detalhe)
      const targetPoints = 2000 * zoomLevel; // Mais pontos com zoom
      const step = Math.max(1, Math.floor(samples.length / targetPoints));
      const downsampled: number[] = [];

      for (let i = 0; i < samples.length; i += step) {
        // Use RMS for better visual representation
        const chunk = samples.slice(i, i + step);
        const rms = Math.sqrt(chunk.reduce((sum, val) => sum + val * val, 0) / chunk.length);
        downsampled.push(rms);
      }

      // Normalize to -1 to 1 range
      const maxAmplitude = Math.max(...downsampled.map(Math.abs));
      const normalized = maxAmplitude > 0
        ? downsampled.map(val => val / maxAmplitude)
        : downsampled;

      await audioContext.close();
      return normalized;
    } catch (error) {
      console.error('Erro ao processar áudio:', error);
      throw error;
    }
  }, [startTime, endTime, zoomLevel]);

  // Load waveforms for all audios
  useEffect(() => {
    const loadWaveforms = async () => {
      // Limpar waveforms ao mudar zoom (reprocessar)
      setWaveforms(new Map());
      
      for (const audio of uniqueAudios) {
        // Set loading state
        setWaveforms(prev => new Map(prev).set(audio.audioId, {
          normalized: [],
          loading: true,
          error: null,
        }));

        try {
          const normalized = await processAudioWaveform(audio);
          setWaveforms(prev => new Map(prev).set(audio.audioId, {
            normalized,
            loading: false,
            error: null,
          }));
        } catch (error) {
          setWaveforms(prev => new Map(prev).set(audio.audioId, {
            normalized: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Erro ao carregar áudio',
          }));
        }
      }
    };

    if (uniqueAudios.length > 0) {
      loadWaveforms();
    }
  }, [uniqueAudios, processAudioWaveform, zoomLevel]);

  // Draw symmetric waveform on canvas
  const drawWaveform = useCallback((canvas: HTMLCanvasElement, data: number[], color: string) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const middle = height / 2;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = '#F9FAFB';
    ctx.fillRect(0, 0, width, height);

    // Draw center line
    ctx.strokeStyle = '#D1D5DB';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, middle);
    ctx.lineTo(width, middle);
    ctx.stroke();

    // Draw amplitude grid lines
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 2]);

    [-0.75, -0.5, -0.25, 0.25, 0.5, 0.75].forEach(amp => {
      const y = middle - (amp * middle * 0.9);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    });

    ctx.setLineDash([]);

    // Draw time markers (every 0.5s)
    ctx.fillStyle = '#9CA3AF';
    ctx.font = '10px monospace';
    const duration = endTime - startTime;
    const pixelsPerSecond = width / duration;
    
    for (let t = 0; t <= duration; t += 0.5) {
      const x = t * pixelsPerSecond;
      ctx.setLineDash([2, 2]);
      ctx.strokeStyle = '#E5E7EB';
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Time label
      const timeLabel = (startTime + t).toFixed(1) + 's';
      ctx.fillText(timeLabel, x + 4, 12);
    }

    if (data.length === 0) return;

    // Draw symmetric waveform (top half)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();

    const step = width / data.length;

    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle - (amplitude * middle * 0.85);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.stroke();

    // Draw symmetric waveform (bottom half - mirrored)
    ctx.beginPath();

    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle + (amplitude * middle * 0.85);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.stroke();

    // Fill area for better visualization
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = color;
    ctx.beginPath();

    // Top half
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle - (amplitude * middle * 0.85);
      if (i === 0) {
        ctx.moveTo(x, middle);
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(width, middle);
    ctx.closePath();
    ctx.fill();

    // Bottom half
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle + (amplitude * middle * 0.85);
      if (i === 0) {
        ctx.moveTo(x, middle);
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(width, middle);
    ctx.closePath();
    ctx.fill();

    ctx.globalAlpha = 1.0;
  }, [startTime, endTime]);

  // Redraw waveforms when data changes
  useEffect(() => {
    waveforms.forEach((waveform, audioId) => {
      const canvas = canvasRefs.current.get(audioId);
      const audioIndex = uniqueAudios.findIndex(a => a.audioId === audioId);
      if (canvas && !waveform.loading && waveform.normalized.length > 0 && audioIndex >= 0) {
        const color = colors[audioIndex % colors.length];
        drawWaveform(canvas, waveform.normalized, color);
      }
    });
  }, [waveforms, uniqueAudios, colors, drawWaveform]);

  // Zoom controls
  const handleZoomIn = () => {
    if (zoomLevel < 4) {
      setZoomLevel(prev => prev + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 1) {
      setZoomLevel(prev => prev - 1);
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Scroll navigation
  const scrollToPosition = (audioId: string, position: 'start' | 'middle' | 'end') => {
    const container = scrollContainerRefs.current.get(audioId);
    if (!container) return;

    const scrollWidth = container.scrollWidth - container.clientWidth;
    let scrollTo = 0;

    switch (position) {
      case 'start':
        scrollTo = 0;
        break;
      case 'middle':
        scrollTo = scrollWidth / 2;
        break;
      case 'end':
        scrollTo = scrollWidth;
        break;
    }

    container.scrollTo({ left: scrollTo, behavior: 'smooth' });
  };

  if (uniqueAudios.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center">
        <svg
          className="mx-auto h-16 w-16 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
          />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Nenhum áudio selecionado
        </h3>
        <p className="text-sm text-gray-600">
          Selecione até 10 áudios na lista ao lado para visualizar e comparar suas waveforms simétricas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Zoom Controls */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              Análise Comparativa de Waveforms
            </h2>
            <p className="text-sm text-gray-700">
              <span className="font-medium">{uniqueAudios.length}</span> áudio(s) • 
              Intervalo: <span className="font-medium">{startTime}s - {endTime}s</span> • 
              Visualização simétrica normalizada
            </p>
          </div>
            <div className="text-right">
            <p className="text-xs text-gray-600 mb-1">Capacidade</p>
            <div className="flex items-center gap-1">
              {[...Array(10)].map((_, i) => (
                <div
                  key={`slot-${i}`}
                  className={`w-6 h-6 rounded ${
                    i < uniqueAudios.length
                      ? 'bg-indigo-600'
                      : 'bg-gray-200'
                  }`}
                  title={`Slot ${i + 1}${i < uniqueAudios.length ? ' (ocupado)' : ' (vazio)'}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-3 pt-3 border-t border-indigo-200">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Zoom:</span>
          </div>
          
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Diminuir zoom"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map(level => (
              <button
                key={level}
                onClick={() => setZoomLevel(level)}
                className={`w-8 h-8 rounded ${
                  zoomLevel === level
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } text-xs font-semibold transition`}
                title={`Zoom ${level}x`}
              >
                {level}x
              </button>
            ))}
          </div>

          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 4}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Aumentar zoom"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <button
            onClick={handleResetZoom}
            className="ml-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            title="Resetar zoom"
          >
            Resetar
          </button>

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Role horizontalmente para navegar</span>
          </div>
        </div>
      </div>

      {/* Waveforms Grid */}
      <div className="space-y-3">
        {uniqueAudios.map((audio, index) => {
          const waveform = waveforms.get(audio.audioId);
          const color = colors[index % colors.length];

          return (
            <div
              key={`${audio.audioId}-${index}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              {/* Audio Info Header */}
              <div className="px-4 pt-4 pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-4 h-4 rounded-full ring-2 ring-white shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                      <h3 className="text-sm font-semibold text-gray-900">
                        {audio.subject_id}
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                        {audio.focus}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-xs text-gray-600">
                      <div>
                        <span className="font-medium text-gray-500">Sexo:</span>{' '}
                        {audio.biological_sex}
                      </div>
                      <div>
                        <span className="font-medium text-gray-500">Qualidade:</span>{' '}
                        {audio.signal_quality}
                      </div>
                      <div>
                        <span className="font-medium text-gray-500">Duração:</span>{' '}
                        {audio.duration_s.toFixed(1)}s
                      </div>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => scrollToPosition(audio.audioId, 'start')}
                      className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
                      title="Ir para o início"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => scrollToPosition(audio.audioId, 'middle')}
                      className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
                      title="Ir para o meio"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </button>
                    <button
                      onClick={() => scrollToPosition(audio.audioId, 'end')}
                      className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
                      title="Ir para o fim"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Waveform Canvas with Horizontal Scroll */}
              <div className="px-4 pb-4">
                {waveform?.loading && (
                  <div className="h-40 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-indigo-600 border-t-transparent mb-2"></div>
                      <p className="text-sm text-gray-600">Processando waveform...</p>
                    </div>
                  </div>
                )}

                {waveform?.error && (
                  <div className="h-40 flex items-center justify-center bg-red-50 rounded-lg border border-red-200">
                    <div className="text-center px-4">
                      <svg
                        className="mx-auto h-8 w-8 text-red-600 mb-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-red-700 font-medium mb-1">Erro ao carregar</p>
                      <p className="text-xs text-red-600">{waveform.error}</p>
                    </div>
                  </div>
                )}

                {!waveform?.loading && !waveform?.error && (
                  <div
                    ref={(el) => {
                      if (el) {
                        scrollContainerRefs.current.set(audio.audioId, el);
                      }
                    }}
                    className="relative overflow-x-auto rounded-lg border border-gray-300 shadow-inner bg-gray-50"
                    style={{ maxHeight: '200px' }}
                  >
                    <canvas
                      ref={(el) => {
                        if (el) {
                          canvasRefs.current.set(audio.audioId, el);
                        }
                      }}
                      width={canvasWidth}
                      height={180}
                      className="border-0"
                    />
                    
                    {/* Scroll indicator */}
                    <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded text-xs text-gray-600 shadow-sm flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Zoom {zoomLevel}x • Role para navegar
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Metodological Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-sm text-amber-800 font-semibold mb-2">
              Notas Metodológicas e Navegação
            </p>
            <ul className="text-sm text-amber-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span><strong>Navegação Horizontal:</strong> Use o scroll do mouse ou trackpad para navegar horizontalmente nas waveforms</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span><strong>Controles de Zoom:</strong> Níveis 1x a 4x para análise detalhada • Marcadores temporais a cada 0.5s</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span><strong>Atalhos de Navegação:</strong> Use os botões ⏮ ⏺ ⏭ para ir rapidamente ao início, meio ou fim</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span><strong>Normalização:</strong> Amplitudes divididas pelo valor máximo absoluto no segmento analisado</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span><strong>RMS (Root Mean Square):</strong> Usado para downsampling, preservando informação energética</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span className="italic">Zoom maior = mais detalhes • Use zoom 2x ou 3x para análise detalhada de padrões</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaveformComparison;
